import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ContentCopy, Check } from '@mui/icons-material';
import { useAppSelector } from '../../store/hooks';

interface ViewQueryHistoryModalProps {
  open: boolean;
  onClose: () => void;
}

const ViewQueryHistoryModal: React.FC<ViewQueryHistoryModalProps> = ({ open, onClose }) => {
  const { selectedHistory, loading } = useAppSelector((state) => state.queryHistory);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // fallback silencioso
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Detalle de Consulta</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : selectedHistory ? (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Consulta del Usuario
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {selectedHistory.query}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  SQL Generado
                </Typography>
                {selectedHistory.generatedSql && (
                  <Tooltip title={copiedField === 'sql' ? 'Copiado' : 'Copiar SQL'}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(selectedHistory.generatedSql, 'sql')}
                    >
                      {copiedField === 'sql' ? (
                        <Check fontSize="small" color="success" />
                      ) : (
                        <ContentCopy fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box
                sx={{
                  mt: 1,
                  mb: 2,
                  p: 2,
                  backgroundColor: '#1e1e1e',
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 300,
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
                    fontSize: '0.85rem',
                    lineHeight: 1.5,
                    color: '#d4d4d4',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {selectedHistory.generatedSql || '(vacío)'}
                </pre>
              </Box>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Prompt
                </Typography>
                {selectedHistory.prompt && (
                  <Tooltip title={copiedField === 'prompt' ? 'Copiado' : 'Copiar Prompt'}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(selectedHistory.prompt, 'prompt')}
                    >
                      {copiedField === 'prompt' ? (
                        <Check fontSize="small" color="success" />
                      ) : (
                        <ContentCopy fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box
                sx={{
                  mt: 1,
                  mb: 2,
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                  overflow: 'auto',
                  maxHeight: 300,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
                    fontSize: '0.82rem',
                    lineHeight: 1.6,
                  }}
                >
                  {selectedHistory.prompt || '(vacío)'}
                </Typography>
              </Box>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Tablas Utilizadas
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedHistory.tablesUsed || '(ninguna)'}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Resultado de Ejecución
              </Typography>
              <Box sx={{ mt: 0.5, mb: 2 }}>
                <Chip
                  label={selectedHistory.executionSuccess ? 'Exitoso' : 'Error'}
                  color={selectedHistory.executionSuccess ? 'success' : 'error'}
                  size="small"
                />
              </Box>
              <Divider />
            </Box>

            {selectedHistory.executionErrorMessage && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mensaje de Error
                </Typography>
                <Typography variant="body1" color="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                  {selectedHistory.executionErrorMessage}
                </Typography>
                <Divider />
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Fecha de Creación
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedHistory.createdAt}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Creado Por
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedHistory.createdBy}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Fecha de Actualización
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedHistory.updatedAt}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Actualizado Por
              </Typography>
              <Typography variant="body1">
                {selectedHistory.updatedBy}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography>No hay registro seleccionado</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewQueryHistoryModal;
